import { z } from 'zod';
import { ASSET_CATEGORIES, categoryLabel } from '../assets/categories.js';
import { pickTri } from '../common/i18n.js';
import { searchWords } from '../common/search-fold.js';
import { SUBJECT_SOURCES } from './review.js';
import { MOVE_ROADS } from './moves.js';
export { MOVE_ROADS, moveRoadsFor } from './moves.js';
// The files foundation's closed vocabularies (ADR-068) — one home, consumed by the
// backend (validation + gates + caps + the search's word→key map), the frontend
// (the folder chips, the picker, the upload flow — the labels injected into i18next
// at boot, the vocabulary law) and any producer (the bot posts the same class /
// folder / topic words).
//
// Two axes, each in one place:
//   class  — the ANCHOR family a file is born on (which gate, lifecycle and upload
//            rules apply) + the special natures; never "where shown". The first
//            segment of the object key — birth provenance, never changed.
//   folder — the closed, per-anchor list of places a DOCUMENT is filed on that
//            anchor; carried on the anchor's link row (the enforced edge — a move
//            re-points it), never in the key. Adding a folder is an architecture
//            decision (ADR-068 §2).
// The nature rule: a described thing (image · video · audio) has NO folder; a filed
// thing (pdf · office · text · cad · archive) has exactly one. Nature is read from
// the folder being empty, never stored. Subjects (what a photo is ABOUT) and
// topics (a category-level word) are links on the file, not classes.
// ── class ───────────────────────────────────────────────────────────────────
export const FILE_CLASSES = [
    'ticket',
    'site',
    'asset',
    'model',
    'user',
    'vehicle',
    'warehouse',
    'project',
    // special natures
    'credential',
    'generated',
    'import_source',
];
export const FileClassSchema = z.enum(FILE_CLASSES);
export const ANCHOR_KINDS = ['ticket', 'site', 'asset', 'model', 'user', 'vehicle', 'warehouse', 'project'];
/** The anchor entity a class is born on. `credential` anchors on an asset (a VPN
 *  profile is the device's secret); `generated` / `import_source` anchor on the
 *  project (their producers are system jobs). */
export const CLASS_ANCHOR = {
    ticket: 'ticket',
    site: 'site',
    asset: 'asset',
    model: 'model',
    user: 'user',
    vehicle: 'vehicle',
    warehouse: 'warehouse',
    project: 'project',
    credential: 'asset',
    generated: 'project',
    import_source: 'project',
};
// ── folders — per anchor, closed ────────────────────────────────────────────
export const SITE_FOLDERS = [
    // legal (restricted — `site.legal:read`)
    'permits',
    'contracts',
    'certificates',
    // technical
    'drawings',
    'as_built',
    'survey',
    'acceptance',
    // operations
    'reports',
    'audits',
    'access',
    'safety',
    'other',
];
/** The site folder's three bands (legal / technical / operations) — the legal
 *  facet's scope and the folder filter's grouping, never printed as headings. */
export const SITE_FOLDER_GROUPS = {
    legal: ['permits', 'contracts', 'certificates'],
    technical: ['drawings', 'as_built', 'survey', 'acceptance'],
    operations: ['reports', 'audits', 'access', 'safety', 'other'],
};
/** The datasheet is the product's; the photos are the proof — the asset files its own paper only. */
export const ASSET_FOLDERS = ['warranty', 'service', 'other'];
export const MODEL_FOLDERS = ['datasheets', 'manuals', 'certificates', 'drawings', 'other'];
export const USER_FOLDERS = ['identity', 'contracts', 'medical', 'certifications'];
export const VEHICLE_FOLDERS = ['papers', 'insurance', 'fines', 'service'];
export const WAREHOUSE_FOLDERS = ['floor_plans', 'certificates', 'safety', 'access'];
export const PROJECT_FOLDERS = ['agreements', 'design', 'procedures', 'reports', 'brand'];
export const FOLDERS_BY_CLASS = {
    site: SITE_FOLDERS,
    asset: ASSET_FOLDERS,
    model: MODEL_FOLDERS,
    user: USER_FOLDERS,
    vehicle: VEHICLE_FOLDERS,
    warehouse: WAREHOUSE_FOLDERS,
    project: PROJECT_FOLDERS,
};
export const FILE_FOLDERS = [
    ...new Set([
        ...SITE_FOLDERS,
        ...ASSET_FOLDERS,
        ...MODEL_FOLDERS,
        ...USER_FOLDERS,
        ...VEHICLE_FOLDERS,
        ...WAREHOUSE_FOLDERS,
        ...PROJECT_FOLDERS,
    ]),
];
export const FileFolderSchema = z.string().refine((f) => FILE_FOLDERS.includes(f), 'unknown folder');
/** The folder words — one home for both ends (the vocabulary law); a key shared by
 *  two anchors (contracts · certificates · drawings · reports · safety · access ·
 *  service · other) reads the same on each. */
export const FOLDER_LABELS = {
    permits: { en: 'Permits', fr: 'Permis', ar: 'التصاريح' },
    contracts: { en: 'Contracts', fr: 'Contrats', ar: 'العقود' },
    certificates: { en: 'Certificates', fr: 'Certificats', ar: 'الشهادات' },
    drawings: { en: 'Drawings', fr: 'Plans', ar: 'المخططات' },
    as_built: { en: 'As-built', fr: 'Dossier d’exécution', ar: 'المخططات التنفيذية' },
    survey: { en: 'Survey', fr: 'Relevé', ar: 'المسح' },
    acceptance: { en: 'Acceptance', fr: 'Réception', ar: 'الاستلام' },
    reports: { en: 'Reports', fr: 'Rapports', ar: 'التقارير' },
    audits: { en: 'Audits', fr: 'Audits', ar: 'عمليات التدقيق' },
    access: { en: 'Access', fr: 'Accès', ar: 'الوصول' },
    safety: { en: 'Safety', fr: 'Sécurité', ar: 'السلامة' },
    other: { en: 'Other', fr: 'Autre', ar: 'أخرى' },
    warranty: { en: 'Warranty', fr: 'Garantie', ar: 'الضمان' },
    service: { en: 'Service', fr: 'Entretien', ar: 'الصيانة' },
    datasheets: { en: 'Datasheets', fr: 'Fiches techniques', ar: 'أوراق البيانات' },
    manuals: { en: 'Manuals', fr: 'Manuels', ar: 'الأدلة' },
    identity: { en: 'Identity papers', fr: 'Pièces d’identité', ar: 'وثائق الهوية' },
    medical: { en: 'Medical', fr: 'Médical', ar: 'الطبي' },
    certifications: { en: 'Certifications', fr: 'Certifications', ar: 'الشهادات المهنية' },
    papers: { en: 'Papers', fr: 'Papiers', ar: 'الأوراق' },
    insurance: { en: 'Insurance', fr: 'Assurance', ar: 'التأمين' },
    fines: { en: 'Fines', fr: 'Amendes', ar: 'المخالفات' },
    floor_plans: { en: 'Floor plans', fr: 'Plans d’étage', ar: 'مخططات الطوابق' },
    agreements: { en: 'Agreements', fr: 'Accords', ar: 'الاتفاقيات' },
    design: { en: 'Design', fr: 'Conception', ar: 'التصميم' },
    procedures: { en: 'Procedures', fr: 'Procédures', ar: 'الإجراءات' },
    brand: { en: 'Brand', fr: 'Marque', ar: 'الهوية البصرية' },
};
export function folderLabel(folder, lang) {
    const tri = FOLDER_LABELS[folder];
    return tri ? pickTri(tri, lang) : folder;
}
/** Is `folder` a legal folder for `cls`? Media (no folder) is fine on every anchor
 *  that has a vocabulary; the special natures take none. */
export function folderAllowed(cls, folder) {
    const allowed = FOLDERS_BY_CLASS[cls];
    if (!allowed)
        return folder == null;
    return folder == null || allowed.includes(folder);
}
// ── restricted folders — a facet key on top of the anchor's gate (ADR-068 §5) ─
// Hidden entirely without the key; writing into / moving out of / renaming needs
// the files verb ∧ the facet key(s) of both folders.
export const RESTRICTED_FOLDERS = {
    site: { permits: 'site.legal:read', contracts: 'site.legal:read', certificates: 'site.legal:read' },
    user: { identity: 'user.documents:read', contracts: 'user.documents:read', medical: 'user.documents:read' },
};
/** The facet key gating `folder` on `cls`, or null when the folder is operational. */
export function facetKeyFor(cls, folder) {
    if (!folder)
        return null;
    return RESTRICTED_FOLDERS[cls]?.[folder] ?? null;
}
// ── topics — what a described thing is about at CATEGORY level (DOMAIN-RULES §7) ─
// A key of the register's category charter, or a word from the short tail no
// asset carries. The ladder: a named asset gives its category for free, so the
// picker never asks twice and a topic row is written only for what no named
// asset implies.
export const FILE_TOPIC_TAIL = ['safety', 'civil', 'access', 'damage', 'general'];
export const TOPIC_TAIL_LABELS = {
    safety: { en: 'Safety', fr: 'Sécurité', ar: 'السلامة' },
    civil: { en: 'Civil works', fr: 'Génie civil', ar: 'الأعمال المدنية' },
    access: { en: 'Access', fr: 'Accès', ar: 'الوصول' },
    damage: { en: 'Damage', fr: 'Dégât', ar: 'تلف' },
    general: { en: 'General', fr: 'Général', ar: 'عام' },
};
export const FILE_TOPICS = [...ASSET_CATEGORIES, ...FILE_TOPIC_TAIL];
export const FileTopicSchema = z.enum(FILE_TOPICS);
export function isFileTopic(v) {
    return FILE_TOPICS.includes(v);
}
export function topicLabel(topic, lang) {
    if (FILE_TOPIC_TAIL.includes(topic))
        return pickTri(TOPIC_TAIL_LABELS[topic], lang);
    if (ASSET_CATEGORIES.includes(topic))
        return categoryLabel(topic, lang);
    return topic;
}
// ── subjects — the sources ladder (review.ts owns the fold) ─────────────────
export const SubjectSourceSchema = z.enum(SUBJECT_SOURCES);
// ── derivatives (thumb · web) — producer-made sizes, a side table, one bucket ──
export const DERIVATIVE_KINDS = ['thumb', 'web'];
export const DerivativeKindSchema = z.enum(DERIVATIVE_KINDS);
/** The gallery's target sizes (the longest edge). The browser resizes on upload;
 *  Telegram's ready-made sizes are picked to match. */
export const DERIVATIVE_MAX_EDGE = { thumb: 400, web: 1280 };
export const DERIVATIVE_MAX_BYTES = { thumb: 512 * 1024, web: 2 * 1024 * 1024 };
// ── status ──────────────────────────────────────────────────────────────────
export const FILE_STATUSES = ['pending', 'committed', 'deleted', 'archived', 'quarantined'];
export const FileStatusSchema = z.enum(FILE_STATUSES);
// ── content families + the sniffed types (ADR-068 §4: the declared type is
// discarded; the first bytes decide) ─────────────────────────────────────────
export const CONTENT_FAMILIES = ['image', 'video', 'audio', 'pdf', 'document', 'text', 'cad', 'archive'];
export const ContentFamilySchema = z.enum(CONTENT_FAMILIES);
/** The described families — media by default (the person may still file one). */
export const MEDIA_FAMILIES = ['image', 'video', 'audio'];
/** The family words — the tile's eyebrow, the type filter's rows, a searchable field. */
export const FAMILY_LABELS = {
    image: { en: 'Photo', fr: 'Photo', ar: 'صورة' },
    video: { en: 'Video', fr: 'Vidéo', ar: 'فيديو' },
    audio: { en: 'Voice note', fr: 'Note vocale', ar: 'ملاحظة صوتية' },
    pdf: { en: 'PDF', fr: 'PDF', ar: 'PDF' },
    document: { en: 'Office document', fr: 'Document bureautique', ar: 'مستند مكتبي' },
    text: { en: 'Text file', fr: 'Fichier texte', ar: 'ملف نصي' },
    cad: { en: 'Drawing', fr: 'Plan', ar: 'مخطط' },
    archive: { en: 'Archive', fr: 'Archive', ar: 'أرشيف' },
};
export function familyLabel(family, lang) {
    return pickTri(FAMILY_LABELS[family], lang);
}
/** The "File type" filter's rows — office folds the two office families. */
export const FILE_TYPE_FILTERS = ['image', 'video', 'audio', 'pdf', 'office', 'cad', 'archive'];
export const FileTypeFilterSchema = z.enum(FILE_TYPE_FILTERS);
/** The type filters that name MEDIA (the flow); the others name documents (the shelf) — the library's two natures. */
export const MEDIA_TYPE_FILTERS = ['image', 'video', 'audio'];
export const FAMILIES_OF_TYPE_FILTER = {
    image: ['image'],
    video: ['video'],
    audio: ['audio'],
    pdf: ['pdf'],
    office: ['document', 'text'],
    cad: ['cad'],
    archive: ['archive'],
};
/** Every content type the sniffer can return, with its family. Anything else is
 *  refused at the first chunk. Office formats are ZIP containers: the sniffer
 *  reports `application/zip`, and the declared EXTENSION (never the declared
 *  type) refines it to the office mime — an attachment either way. */
export const SNIFFED_TYPES = {
    'image/jpeg': 'image',
    'image/png': 'image',
    'image/webp': 'image',
    'image/gif': 'image',
    'image/heic': 'image',
    'video/mp4': 'video',
    'video/quicktime': 'video',
    'video/webm': 'video',
    'video/3gpp': 'video',
    'audio/mpeg': 'audio',
    'audio/ogg': 'audio',
    'audio/mp4': 'audio',
    'audio/wav': 'audio',
    'audio/aac': 'audio',
    'application/pdf': 'pdf',
    'application/zip': 'archive',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'document',
    'text/plain': 'text',
    'text/csv': 'text',
    'application/acad': 'cad',
    'application/dxf': 'cad',
};
/** Classes on which EVERY file is FILED — the nature rule per anchor (the
 *  2026-09-05 ruling): a product's library is reference paper, so a photo or a
 *  video on a model is content in a folder, never media — no flow, no
 *  classification, no review. Announce and commit require the folder outright. */
export const ALWAYS_FILED_CLASSES = ['model'];
/** Which families a class admits. Captures are media; documents are documents;
 *  a credential file is a small text container (an .ovpn / a WireGuard conf). */
export const CLASS_FAMILIES = {
    ticket: ['image', 'video', 'audio', 'pdf', 'document', 'text'],
    site: ['image', 'video', 'audio', 'pdf', 'document', 'text', 'cad', 'archive'],
    asset: ['image', 'video', 'audio', 'pdf', 'document', 'text'],
    // The site library's whole set (the ruling above) — filed, whatever the family.
    model: ['image', 'video', 'audio', 'pdf', 'document', 'text', 'cad', 'archive'],
    user: ['pdf', 'image', 'document'],
    vehicle: ['pdf', 'image', 'document'],
    warehouse: ['pdf', 'image', 'document', 'cad'],
    project: ['pdf', 'image', 'document', 'text', 'archive'],
    credential: ['text'],
    generated: ['pdf', 'document', 'text', 'archive'],
    import_source: ['document', 'text'],
};
const MB = 1024 * 1024;
/** Size caps — per family (enforced while streaming; a too-large file is cut at
 *  the cap) and a per-class ceiling. Numbers are the availability rule's
 *  (SECURITY §Files): a phone photo is a few MB, a site drawing can be large. */
export const FAMILY_MAX_BYTES = {
    image: 25 * MB,
    video: 200 * MB,
    audio: 50 * MB,
    pdf: 100 * MB,
    document: 100 * MB,
    text: 10 * MB,
    cad: 500 * MB,
    archive: 500 * MB,
};
export const CLASS_MAX_BYTES = {
    ticket: 200 * MB,
    site: 500 * MB,
    asset: 200 * MB,
    model: 500 * MB, // mirrors the site (drawings and archives ride the model too)
    user: 50 * MB,
    vehicle: 50 * MB,
    warehouse: 500 * MB,
    project: 500 * MB,
    credential: 1 * MB,
    generated: 500 * MB,
    import_source: 50 * MB,
};
/** Families the files origin may render INLINE (previews). Everything else is an
 *  attachment always (SECURITY §Files — never SVG / HTML / office / text inline). */
export const INLINE_FAMILIES = ['image', 'video', 'audio', 'pdf'];
/** Classes that never preview and never ship a derivative. */
export const NEVER_PREVIEW_CLASSES = ['credential', 'import_source'];
/** Classes whose previews AUDIT (restricted-folder files audit via their folder). */
export const PREVIEW_AUDITED_CLASSES = ['user'];
/** The "sensitive" test for read-audit: user documents and restricted folders audit
 *  every preview; every class audits every download. */
export function previewAudits(cls, folder) {
    return PREVIEW_AUDITED_CLASSES.includes(cls) || facetKeyFor(cls, folder) !== null;
}
/** Signed preview links: short-lived by rule (ADR-068 §4). */
export const PREVIEW_LINK_TTL_SECONDS = 60;
/** One batched mint per visible chunk (a flow page is 48 tiles). */
export const PREVIEW_LINK_BATCH_MAX = 100;
// ── the acts' small constants ───────────────────────────────────────────────
/** A note's body — the app-level cap on the TEXT column. */
export const NOTE_MAX_CHARS = 2000;
/** The recycle bin's grace window before the sweeper purges (the project setting
 *  arrives with the retention design; one number until then). */
export const BIN_GRACE_DAYS = 30;
/** The roads a move may take: a folder here · another site · an asset (here or there) — the rule lives in `moves.ts`. */
export const MoveRoadSchema = z.enum(MOVE_ROADS);
/** The decisions the classification verb takes. */
export const CLASSIFY_DECISIONS = ['confirm', 'change', 'nothing'];
export const ClassifyDecisionSchema = z.enum(CLASSIFY_DECISIONS);
/** The flow's page — 96 under the pager: a grid of tiles fills whole rows at 2 · 3 · 4 · 6 · 8 · 12 columns, under the
 *  wire's cap of 100 (ADR-078 §4 — the flow is the band stream with every band open). */
export const FLOW_PAGE_SIZE = 96;
/** The flow's dividers — the calendar grains a paged stream in time order is cut by (ADR-078 §4): every band open, the
 *  counts true from one GROUP BY over the whole filtered library, the days cut in the screen's zone. An axis that is
 *  not contiguous in time order (a person, a type) is a filter, never a divider. */
export const MEDIA_GRAINS = ['day', 'month', 'year'];
// ── the search's word → key map (the server's half of the shared contract) ──
// The client matches a token against the LABELS it renders; the server holds
// keys, so it maps a folded token to every vocabulary key whose label — in any
// platform language — has a word starting with it, then matches the keys.
function keysMatching(token, labels) {
    const out = [];
    for (const [key, tri] of Object.entries(labels)) {
        if (searchWords([tri.en, tri.fr, tri.ar]).some((w) => w.startsWith(token)))
            out.push(key);
    }
    return out;
}
/** `token` is already folded (`searchTokens`). */
export function vocabularyKeysFor(token) {
    const categories = ASSET_CATEGORIES.filter((c) => searchWords([categoryLabel(c, 'en'), categoryLabel(c, 'fr'), categoryLabel(c, 'ar')]).some((w) => w.startsWith(token)));
    return {
        topics: [...categories, ...keysMatching(token, TOPIC_TAIL_LABELS)],
        folders: keysMatching(token, FOLDER_LABELS),
        families: keysMatching(token, FAMILY_LABELS),
    };
}
